import { createContext, useContext } from 'react';
import { Outlet, useParams } from 'react-router';
import { isOrgAdmin } from '../components/Layout';
import { ErrorNote, FreshnessBadge, Loading, PageHeader, Tabs } from '../components/ui';
import { ApiError, type Plant as PlantData } from '../lib/api';
import { freshness } from '../lib/format';
import { useMe, usePlant } from '../lib/queries';

const PlantContext = createContext<PlantData | null>(null);

export function usePlantContext(): PlantData {
  const p = useContext(PlantContext);
  if (!p) throw new Error('usePlantContext outside a plant page');
  return p;
}

export function Plant() {
  const { slug = '' } = useParams();
  const plant = usePlant(slug);
  const me = useMe();

  if (plant.isPending) return <Loading />;
  if (plant.error) {
    return plant.error instanceof ApiError && plant.error.status === 404
      ? <ErrorNote error={new Error(`No plant ${slug}, or you don't have access to it.`)} />
      : <ErrorNote error={plant.error} />;
  }
  const p = plant.data;
  const f = freshness(p.sync);
  const admin = !!me.data && isOrgAdmin(me.data, p.organization.id);

  return (
    <PlantContext.Provider value={p}>
      <PageHeader
        title={p.name}
        subtitle={<span>{p.organization.name} · times in {p.timezone.replace('_', ' ')}</span>}
        actions={<FreshnessBadge state={f.state} label={f.label} detail={f.detail} />}
      />
      <Tabs
        items={[
          { to: `/plants/${p.slug}`, label: 'Overview', end: true },
          { to: `/plants/${p.slug}/charts`, label: 'Charts' },
          { to: `/plants/${p.slug}/alarms`, label: `Alarms${p.activeAlarms.count ? ` (${p.activeAlarms.count})` : ''}` },
          ...(admin ? [{ to: `/plants/${p.slug}/devices`, label: 'Devices' }] : []),
        ]}
      />
      <Outlet />
    </PlantContext.Provider>
  );
}
